import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  Alert,
  Linking,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import {
  deviceCalendarsAvailable,
  getDeviceCalendarPermissionState,
  listDeviceCalendars,
  requestDeviceCalendarPermission,
} from '../calendar/deviceCalendars';
import {
  availableSelectedCalendarIds,
  DeviceCalendarOption,
  DeviceCalendarPermissionState,
} from '../calendar/deviceCalendarsTypes';
import {
  disconnectDeviceCalendarConnection,
  enableDeviceCalendarConnection,
  isDeviceCalendarConnectionEnabled,
} from '../calendar/calendarConnection';
import { syncMeetupsToSystemCalendar } from '../calendar/acceptedMeetupSync';
import { storage } from '../services/storage';
import { api, GoogleCalendarConnectionStatus, MicrosoftCalendarConnectionStatus } from '../services/api';
import { fontSize, spacing, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { uppercaseForDisplayLanguage } from '../utils/locale';
import { InfoButton, InfoText, useInfoSet } from '../components/InfoHint';

type Props = {
  token: string;
  userId: number;
  onBack: () => void;
};


export default function CalendarConnectionsScreen({ token, userId, onBack }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [permission, setPermission] = useState<DeviceCalendarPermissionState>('undetermined');
  const [connected, setConnected] = useState(false);
  const [calendars, setCalendars] = useState<DeviceCalendarOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [googleStatus, setGoogleStatus] = useState<GoogleCalendarConnectionStatus | null>(null);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleDisconnecting, setGoogleDisconnecting] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleSavingId, setGoogleSavingId] = useState<string | null>(null);
  const [googleFailed, setGoogleFailed] = useState(false);
  // 7.14 / D-654 — açıklamalar ⓘ arkasında ve ekran her açıldığında kapalı.
  // D-220'nin kalıcı "kapat" tercihi bunun içinde eridi: artık hiçbiri baştan açık değil.
  const info = useInfoSet();
  const [microsoftStatus, setMicrosoftStatus] = useState<MicrosoftCalendarConnectionStatus | null>(null);
  const [microsoftConnecting, setMicrosoftConnecting] = useState(false);
  const [hiddenExternalEventCount, setHiddenExternalEventCount] = useState(0);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setFailed(false);
    try {
      let [savedIds, nextPermission, nextConnected, hiddenEventKeys] = await Promise.all([
        storage.getSelectedCalendarIds(userId),
        getDeviceCalendarPermissionState(),
        isDeviceCalendarConnectionEnabled(userId),
        storage.getHiddenExternalCalendarEventKeys(userId),
      ]);
      setHiddenExternalEventCount(hiddenEventKeys.length);
      if (nextConnected && nextPermission === 'denied') {
        await disconnectDeviceCalendarConnection(userId);
        savedIds = [];
        nextConnected = false;
      }
      setPermission(nextPermission);
      setConnected(nextConnected);
      if (!nextConnected || nextPermission !== 'granted') {
        setCalendars([]);
        setSelectedIds(savedIds);
        return;
      }
      const nextCalendars = await listDeviceCalendars();
      const validIds = availableSelectedCalendarIds(savedIds, nextCalendars);
      setCalendars(nextCalendars);
      setSelectedIds(validIds);
      if (validIds.length !== savedIds.length) {
        await storage.setSelectedCalendarIds(userId, validIds);
      }
    } catch {
      setFailed(true);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [userId]);

  const loadGoogle = useCallback(async () => {
    setGoogleFailed(false);
    try {
      // Microsoft durumu Google'dan bağımsız okunur: biri yapılandırılmamışsa diğeri yine görünür.
      // Bağlıysa eşitleme de aynı bağımsızlıkla denenir; hata Google kartını düşürmez (D-583).
      api.getMicrosoftCalendarConnection(token)
        .then(async (status) => {
          setMicrosoftStatus(status);
          if (!status.connected) return;
          const lastSync = status.connection?.lastSyncedAt
            ? new Date(status.connection.lastSyncedAt).getTime()
            : 0;
          if (lastSync >= Date.now() - 60_000) return;
          try {
            await api.syncMicrosoftCalendar(token);
            setMicrosoftStatus(await api.getMicrosoftCalendarConnection(token));
          } catch {
            // Eşitleme başarısız olabilir; okunmuş durum yine doğrudur. Kartı boşaltmak
            // "bağlı değil" izlenimi verirdi — bağlantı duruyor, yalnız verisi bayat.
          }
        })
        .catch(() => setMicrosoftStatus(null));
      let status = await api.getGoogleCalendarConnection(token);
      setGoogleStatus(status);
      if (status.connected) {
        const lastSync = status.connection?.lastSyncedAt
          ? new Date(status.connection.lastSyncedAt).getTime()
          : 0;
        if (lastSync < Date.now() - 60_000) {
          setGoogleSyncing(true);
          await api.syncGoogleCalendar(token);
          status = await api.getGoogleCalendarConnection(token);
          setGoogleStatus(status);
        }
      }
    } catch {
      setGoogleFailed(true);
    } finally {
      setGoogleSyncing(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    loadGoogle();
  }, [load, loadGoogle]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        load(false);
        loadGoogle();
      }
    });
    return () => subscription.remove();
  }, [load, loadGoogle]);

  async function connectMicrosoft() {
    if (!token) return;
    setMicrosoftConnecting(true);
    try {
      const result = await api.startMicrosoftCalendarAuthorization(token, lang);
      await Linking.openURL(result.authorizationUrl);
    } catch {
      Alert.alert(t('common.error'), t('calendar.microsoftConnectionFailed'));
    } finally {
      setMicrosoftConnecting(false);
    }
  }

  async function connectGoogle() {
    if (!googleStatus?.available || googleConnecting) return;
    setGoogleConnecting(true);
    try {
      const result = await api.startGoogleCalendarAuthorization(token, lang);
      await Linking.openURL(result.authorizationUrl);
    } catch {
      Alert.alert(t('common.error'), t('calendar.googleConnectionFailed'));
    } finally {
      setGoogleConnecting(false);
    }
  }

  function disconnectGoogle() {
    if (googleDisconnecting) return;
    Alert.alert(
      t('calendar.googleDisconnectTitle'),
      t('calendar.googleDisconnectBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('calendar.disconnect'),
          style: 'destructive',
          onPress: async () => {
            setGoogleDisconnecting(true);
            try {
              await api.disconnectGoogleCalendar(token);
              await loadGoogle();
            } catch {
              Alert.alert(t('common.error'), t('calendar.googleDisconnectFailed'));
            } finally {
              setGoogleDisconnecting(false);
            }
          },
        },
      ]
    );
  }

  async function toggleGoogleCalendar(calendarId: string, selected: boolean) {
    if (googleSavingId) return;
    const previous = googleStatus;
    setGoogleStatus((current) => current?.connection ? {
      ...current,
      connection: {
        ...current.connection,
        calendars: current.connection.calendars.map((calendar) =>
          calendar.id === calendarId ? { ...calendar, selected } : calendar
        ),
      },
    } : current);
    setGoogleSavingId(calendarId);
    let selectionSaved = false;
    try {
      await api.selectGoogleCalendar(token, calendarId, selected);
      selectionSaved = true;
      if (selected) await api.syncGoogleCalendar(token, true);
      setGoogleStatus(await api.getGoogleCalendarConnection(token));
    } catch {
      if (selectionSaved) {
        try {
          setGoogleStatus(await api.getGoogleCalendarConnection(token));
        } catch {
          // Seçim sunucuda kaydedildi; geçici durum yenileme hatasında iyimser görünüm korunur.
        }
        Alert.alert(t('common.error'), t('calendar.externalEventsFailed'));
      } else {
        setGoogleStatus(previous);
        Alert.alert(t('common.error'), t('calendar.googleSelectionSaveFailed'));
      }
    } finally {
      setGoogleSavingId(null);
    }
  }

  async function restoreHiddenExternalEvents() {
    try {
      await storage.clearHiddenExternalCalendarEvents(userId);
      setHiddenExternalEventCount(0);
    } catch {
      Alert.alert(t('common.error'), t('calendar.restoreHiddenExternalEventsFailed'));
    }
  }

  async function connect() {
    if (permission === 'denied') {
      Linking.openSettings().catch(() => Alert.alert(t('common.error')));
      return;
    }
    setRequesting(true);
    try {
      const nextPermission = permission === 'granted'
        ? permission
        : await requestDeviceCalendarPermission();
      setPermission(nextPermission);
      if (nextPermission === 'granted') {
        await enableDeviceCalendarConnection(userId);
        setConnected(true);
        // Bağlantı açıldığı anda zaten kabul edilmiş gelecekteki kayıtları da
        // yaz. Sistem izin penceresinden dönüşte kök AppState dinleyicisi,
        // bağlantı bayrağı henüz açılmadan çalışmış olabilir; yalnız sonraki
        // uygulama açılışını veya Takvim sekmesini beklemek otomatik yazım
        // sözleşmesini ihlal eder. Hata bağlantıyı geri almaz; kök eşitleme
        // oturum/ön-plan dönüşünde güvenle yeniden dener.
        syncMeetupsToSystemCalendar(token, userId, {
          meetupTitle: (name) => t('calendar.systemEventMeetupTitle', { name }),
          callTitle: (name) => t('calendar.systemEventCallTitle', { name }),
          notes: t('calendar.systemEventNotes'),
        }).catch(() => {});
        await load(false);
      }
    } catch {
      Alert.alert(t('common.error'), t('calendar.connectionFailed'));
    } finally {
      setRequesting(false);
    }
  }

  function disconnect() {
    Alert.alert(
      t('calendar.disconnectTitle'),
      t('calendar.disconnectBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('calendar.disconnect'),
          style: 'destructive',
          onPress: async () => {
            setRequesting(true);
            try {
              await disconnectDeviceCalendarConnection(userId);
              setConnected(false);
              setCalendars([]);
              setSelectedIds([]);
            } catch {
              Alert.alert(t('common.error'), t('calendar.disconnectFailed'));
            } finally {
              setRequesting(false);
            }
          },
        },
      ]
    );
  }

  async function toggleCalendar(calendarId: string, enabled: boolean) {
    if (savingId) return;
    const previous = selectedIds;
    const next = enabled
      ? [...new Set([...selectedIds, calendarId])]
      : selectedIds.filter((id) => id !== calendarId);
    setSelectedIds(next);
    setSavingId(calendarId);
    try {
      await storage.setSelectedCalendarIds(userId, next);
    } catch {
      setSelectedIds(previous);
      Alert.alert(t('common.error'), t('calendar.selectionSaveFailed'));
    } finally {
      setSavingId(null);
    }
  }

  const permissionStateLabel = connected && permission === 'granted'
    ? t('calendar.permissionGranted')
    : permission === 'denied'
      ? t('calendar.permissionDenied')
      : permission === 'unavailable'
        ? t('calendar.permissionUnavailable')
        : t('calendar.permissionNotRequested');

  return (
    <View style={styles.container}>
      <PageHeader
        title={t('calendar.connectionsTitle')}
        onBack={onBack}
        floating
        action={(
          <InfoButton
            open={info.isOpen('page')}
            onPress={() => info.toggle('page')}
            subject={t('calendar.connectionsTitle')}
            testID="calendar-connections-info"
          />
        )}
      />

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={colors.black} /></View>
      ) : (
        <ScrollView contentContainerStyle={[styles.content, { paddingTop: screenHeaderContentInset(insets.top) }]}>
          <InfoText open={info.isOpen('page')} text={t('calendar.connectionsInfo')} style={styles.description} testID="calendar-connections-description" />
          <InfoText open={info.isOpen('page')} text={t('calendar.connectionPrivacyInfo')} style={styles.description} testID="calendar-connections-privacy" />
          <Text style={styles.sectionLabel}>
            {uppercaseForDisplayLanguage(t('calendar.onlineCalendars'), lang)}
          </Text>
          <View style={styles.section}>
            <View style={styles.permissionRow}>
              <View style={styles.permissionIcon}>
                <Ionicons name="logo-google" size={21} color={colors.black} />
              </View>
              <View style={styles.settingCopy}>
                <View style={styles.titleRow}>
                  <Text style={styles.settingTitle}>{t('calendar.googleCalendar')}</Text>
                  <InfoButton open={info.isOpen('google')} onPress={() => info.toggle('google')} subject={t('calendar.googleCalendar')} testID="calendar-google-info" />
                </View>
                <Text testID="google-calendar-status" style={styles.settingHint}>
                  {!googleStatus
                    ? t(googleFailed ? 'discover.failed' : 'common.loading')
                    : googleStatus.connected
                    ? t('calendar.providerConnected')
                    : googleStatus?.available
                      ? t('calendar.providerNotConnected')
                      : t('calendar.providerUnavailable')}
                </Text>
              </View>
              {!!googleStatus?.available && !googleStatus.connected && (
                <CompactActionButton
                  label={t('calendar.connect')}
                  onPress={connectGoogle}
                  loading={googleConnecting}
                  style={styles.rowAction}
                  testID="calendar-connect"
                />
              )}
              {!!googleStatus?.connected && (
                <CompactActionButton
                  label={t('calendar.disconnect')}
                  onPress={disconnectGoogle}
                  loading={googleDisconnecting}
                  variant="danger"
                  style={styles.rowAction}
                />
              )}
            </View>
            <InfoText open={info.isOpen('google')} text={t('calendar.googlePrivacyInfo')} style={styles.cardInfo} testID="calendar-google-info-text" />
            {!!googleStatus?.connected && (
              <View style={styles.googleCalendars}>
                <Text style={styles.inlineLabel}>{t('calendar.selectCalendars')}</Text>
                {googleSyncing ? (
                  <ActivityIndicator style={styles.inlineLoader} color={colors.black} />
                ) : (googleStatus.connection?.calendars.length ?? 0) === 0 ? (
                  <Text style={styles.empty}>{t('calendar.googleNoCalendars')}</Text>
                ) : googleStatus.connection!.calendars.map((calendar, index) => (
                  <View key={calendar.id} style={[styles.calendarRow, index > 0 && styles.rowBorder]}>
                    <View style={[styles.colorDot, { backgroundColor: calendar.color || colors.gray400 }]} />
                    <View style={styles.settingCopy}>
                      <Text style={styles.settingTitle}>{calendar.title}</Text>
                      {!!calendar.timeZone && <Text style={styles.settingHint}>{calendar.timeZone}</Text>}
                    </View>
                    <Switch
                      value={calendar.selected}
                      accessibilityLabel={calendar.title}
                      onValueChange={(value) => toggleGoogleCalendar(calendar.id, value)}
                      disabled={googleSavingId !== null || googleSyncing}
                      trackColor={{ false: colors.gray200, true: colors.black }}
                      thumbColor={colors.white}
                    />
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.section}>
            <View style={styles.permissionRow}>
              <View style={styles.permissionIcon}>
                <Ionicons name="logo-microsoft" size={21} color={colors.black} />
              </View>
              <View style={styles.settingCopy}>
                <View style={styles.titleRow}>
                  <Text style={styles.settingTitle}>{t('calendar.microsoftCalendar')}</Text>
                  <InfoButton open={info.isOpen('microsoft')} onPress={() => info.toggle('microsoft')} subject={t('calendar.microsoftCalendar')} testID="calendar-microsoft-info" />
                </View>
                <Text testID="microsoft-calendar-status" style={styles.settingHint}>
                  {!microsoftStatus
                    ? t('common.loading')
                    : microsoftStatus.connected
                    ? t('calendar.providerConnected')
                    : microsoftStatus.available
                      ? t('calendar.providerNotConnected')
                      : t('calendar.providerUnavailable')}
                </Text>
              </View>
              {!!microsoftStatus?.available && !microsoftStatus.connected && (
                <CompactActionButton
                  label={t('calendar.connect')}
                  onPress={connectMicrosoft}
                  loading={microsoftConnecting}
                  style={styles.rowAction}
                  testID="calendar-connect-microsoft"
                />
              )}
            </View>
            {/*
              No disconnect button, and the note says where to go instead. nosubject cannot
              revoke a Microsoft grant — the provider offers nothing scoped to one application
              (D-479) — so a button here would claim something it cannot do.
            */}
            <InfoText open={info.isOpen('microsoft')} text={t('calendar.microsoftInfo')} style={styles.cardInfo} testID="calendar-microsoft-info-text" />
          </View>

          <Text style={styles.sectionLabel}>
            {uppercaseForDisplayLanguage(t('calendar.deviceCalendar'), lang)}
          </Text>
          <View style={styles.section}>
            <View style={styles.permissionRow}>
              <View style={styles.permissionIcon}>
                <Ionicons name="calendar-outline" size={22} color={colors.black} />
              </View>
              <View style={styles.settingCopy}>
                <View style={styles.titleRow}>
                  <Text style={styles.settingTitle}>{t('calendar.deviceCalendar')}</Text>
                  <InfoButton open={info.isOpen('device')} onPress={() => info.toggle('device')} subject={t('calendar.deviceCalendar')} testID="calendar-device-info" />
                </View>
                <Text style={styles.settingHint}>{permissionStateLabel}</Text>
              </View>
              {connected && permission === 'granted' ? (
                <CompactActionButton
                  label={t('calendar.disconnect')}
                  onPress={disconnect}
                  loading={requesting}
                  variant="danger"
                  style={styles.rowAction}
                />
              ) : permission !== 'unavailable' && (
                <CompactActionButton
                  label={permission === 'denied'
                    ? t('calendar.openSettings')
                    : t('calendar.connect')}
                  onPress={connect}
                  loading={requesting}
                  style={styles.rowAction}
                  testID="calendar-device-connect"
                />
              )}
            </View>
            <InfoText open={info.isOpen('device')} text={t('calendar.devicePermissionInfo')} style={styles.cardInfo} testID="calendar-device-info-text" />
          </View>

          {connected && permission === 'granted' && (
            <>
              <Text style={styles.sectionLabel}>
                {uppercaseForDisplayLanguage(t('calendar.selectCalendars'), lang)}
              </Text>
              <View style={styles.section}>
                {calendars.length === 0 ? (
                  <Text style={styles.empty}>{t('calendar.noDeviceCalendars')}</Text>
                ) : calendars.map((calendar, index) => (
                  <View
                    key={calendar.id}
                    style={[styles.calendarRow, index > 0 && styles.rowBorder]}>
                    <View
                      style={[
                        styles.colorDot,
                        { backgroundColor: calendar.color || colors.gray400 },
                      ]}
                    />
                    <View style={styles.settingCopy}>
                      <Text style={styles.settingTitle}>{calendar.title}</Text>
                      {!!calendar.sourceName && (
                        <Text style={styles.settingHint}>{calendar.sourceName}</Text>
                      )}
                    </View>
                    <Switch
                      value={selectedIds.includes(calendar.id)}
                      accessibilityLabel={calendar.title}
                      onValueChange={(value) => toggleCalendar(calendar.id, value)}
                      disabled={savingId !== null}
                      trackColor={{ false: colors.gray200, true: colors.black }}
                      thumbColor={colors.white}
                    />
                  </View>
                ))}
              </View>
              <TouchableOpacity
                style={styles.settingsLink}
                onPress={() => Linking.openSettings().catch(() => Alert.alert(t('common.error')))}>
                <Text style={styles.settingsLinkText}>{t('calendar.manageSystemPermission')}</Text>
                <Text style={styles.disclosure}>›</Text>
              </TouchableOpacity>
            </>
          )}

          {(failed || googleFailed) && (
            <CompactActionButton
              variant="plain"
              label={t('common.retry')}
              style={styles.retryButton}
              testID="calendar-retry"
              onPress={() => {
                load();
                loadGoogle();
              }}
            />
          )}

          {hiddenExternalEventCount > 0 && (
            <TouchableOpacity
              style={styles.settingsLink}
              accessibilityRole="button"
              onPress={restoreHiddenExternalEvents}>
              <View style={styles.restoreHiddenCopy}>
                <Text style={styles.settingsLinkText}>
                  {t('calendar.restoreHiddenExternalEvents')}
                </Text>
                <Text style={styles.settingHint}>
                  {t('calendar.hiddenExternalEventCount', { count: hiddenExternalEventCount })}
                </Text>
              </View>
              <Text style={styles.disclosure}>›</Text>
            </TouchableOpacity>
          )}

          {!deviceCalendarsAvailable && (
            <Text style={styles.notice}>{t('calendar.newBuildRequired')}</Text>
          )}
        </ScrollView>
      )}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  description: { fontSize: fontSize.sm, color: colors.gray600, marginBottom: spacing.sm },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  sectionLabel: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  section: {
    backgroundColor: colors.gray100,
    borderRadius: radius.card,
    paddingHorizontal: spacing.md,
  },
  permissionRow: {
    minHeight: 72,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  permissionIcon: {
    width: 36,
    height: 36,
    borderRadius: radius.control,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingCopy: { flex: 1 },
  restoreHiddenCopy: { flex: 1 },
  settingTitle: { fontSize: fontSize.md, color: colors.black, fontWeight: '500', flexShrink: 1 },
  titleRow: { flexDirection: 'row', alignItems: 'center' },
  settingHint: { fontSize: fontSize.sm, color: colors.gray400, marginTop: 2 },
  // D-410 — geometri ve dokunma hedefi `CompactActionButton`'da; burada yalnız
  // satır içindeki genişlik sınırı kalır. Eski 38 px yükseklik bileşenin 44 pt
  // sözleşmesiyle değişti.
  rowAction: { minWidth: 82, maxWidth: 132, flexShrink: 1 },
  // 7.14 / D-654 — kartın içinde, ⓘ ile açılan açıklama.
  cardInfo: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.gray200,
    paddingVertical: spacing.sm,
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 19,
  },
  googleCalendars: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.gray200,
    paddingBottom: spacing.sm,
  },
  inlineLabel: {
    color: colors.gray400,
    fontSize: fontSize.sm,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
  },
  inlineLoader: { marginVertical: spacing.md },
  calendarRow: {
    minHeight: 62,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowBorder: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.gray200 },
  colorDot: { width: 12, height: 12, borderRadius: 6 },
  empty: { color: colors.gray400, fontSize: fontSize.md, paddingVertical: spacing.lg },
  settingsLink: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  settingsLinkText: { flex: 1, color: colors.black, fontSize: fontSize.md },
  disclosure: { color: colors.gray400, fontSize: fontSize.xl },
  retryButton: { alignSelf: 'center', padding: spacing.md },
  retryText: { color: colors.black, fontSize: fontSize.md, fontWeight: '600' },
  notice: {
    color: colors.gray400,
    fontSize: fontSize.sm,
    lineHeight: 19,
    marginTop: spacing.lg,
  },
});
